import { useState, useEffect } from "react";
import {
  VStack,
  Input,
  Textarea,
  Button,
  Heading,
  Text,
  NumberInput,
  NumberInputField,
  FormLabel,
  FormControl,
  FormHelperText,
  Box,
  useToast,
  HStack,
  Checkbox,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Badge,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
} from "@chakra-ui/react";
import {
  AttachmentIcon,
  EditIcon,
  CheckIcon,
  CloseIcon,
  ChatIcon,
  InfoIcon,
  CalendarIcon,
  SettingsIcon,
  StarIcon,
  WarningIcon,
  ExternalLinkIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import styled from "styled-components";
import { DragDrop, LogoFile } from "../DragDrop";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import axios from "axios";
import { rootUrl } from "../../api/rootUrl";
import {
  Campaign,
  updateCampaign,
  deleteCampaign,
  getRecipientCount,
} from "../../api/admin/adminCampaign";
import { ImageAssetPicker } from "./ImageAssetPicker";
import { MessageBuilder } from "./MessageBuilder";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

const Container = styled.div`
  padding: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0.75rem;
    padding-bottom: 2rem;
  }
`;

const Section = styled.div`
  margin-bottom: 1rem;
  width: 100%;
  max-width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const ImagePreview = styled.img<{ $square?: boolean }>`
  width: ${(props) => (props.$square ? "100px" : "200px")};
  height: ${(props) => (props.$square ? "100px" : "auto")};
  max-height: ${(props) => (props.$square ? "100px" : "150px")};
  object-fit: cover;
  border-radius: 8px;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    width: ${(props) => (props.$square ? "80px" : "150px")};
    height: ${(props) => (props.$square ? "80px" : "auto")};
    max-height: ${(props) => (props.$square ? "80px" : "120px")};
  }
`;

const SummaryBox = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.25rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const DetailItem = styled.div`
  margin-bottom: 0.75rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const EditableSectionBox = styled.div<{ $isEditing: boolean; $color?: string }>`
  background: ${(props) =>
    props.$isEditing ? "white" : `${props.$color}08` || "#fafafa"};
  border: 1px solid ${(props) => (props.$isEditing ? "#3182ce" : "#e2e8f0")};
  border-left: 4px solid ${(props) => props.$color || "#cbd5e0"};
  border-radius: 8px;
  padding: ${(props) => (props.$isEditing ? "1.25rem" : "1rem")};
  margin-bottom: 0.75rem;
  transition: all 0.2s;
  box-shadow: ${(props) =>
    props.$isEditing
      ? "0 4px 12px rgba(0, 0, 0, 0.08)"
      : "0 1px 3px rgba(0, 0, 0, 0.05)"};

  @media (max-width: 768px) {
    padding: ${(props) => (props.$isEditing ? "0.75rem" : "0.75rem")};
    margin-bottom: 0.5rem;
  }
`;

interface Props {
  campaign: Campaign;
  selectedClientId: string;
  onCampaignUpdate: (campaign: Campaign | null) => void;
}

type EditableSection = "basic" | "timing" | "branding" | "features" | null;

export const CampaignEditForm = ({
  campaign,
  selectedClientId,
  onCampaignUpdate,
}: Props) => {
  const { adminToken } = useAdminAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [editingSection, setEditingSection] = useState<EditableSection>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const cancelRef = useRef(null);

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isPickerOpen,
    onOpen: onPickerOpen,
    onClose: onPickerClose,
  } = useDisclosure();
  const [currentImageType, setCurrentImageType] = useState<
    "logo" | "alertImage"
  >("logo");

  // Form fields (current editing values)
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [goalAmount, setGoalAmount] = useState("0");
  const [expires, setExpires] = useState("");
  const [showCountdown, setShowCountdown] = useState(true);
  const [tagline, setTagline] = useState("");
  const [colorTheme, setColorTheme] = useState("#F4B942");
  const [logo, setLogo] = useState("");

  const [enableAlertBanner, setEnableAlertBanner] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertImage, setAlertImage] = useState("");
  const [alertButtonLabel, setAlertButtonLabel] = useState("");

  const [enableMatchingDonors, setEnableMatchingDonors] = useState(false);
  const [matchPoolAmount, setMatchPoolAmount] = useState("0");
  const [hideDonationAmounts, setHideDonationAmounts] = useState(false);

  // Original values (for reverting)
  const [originalValues, setOriginalValues] = useState<any>({});

  // Cache-busting timestamp for images
  const [imageCacheBuster, setImageCacheBuster] = useState(Date.now());

  useEffect(() => {
    if (campaign) {
      // Helper to convert UTC date to local datetime string for datetime-local input
      const toLocalDateTimeString = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      const values = {
        name: campaign.name || "",
        description: campaign.description || "",
        goalAmount: String((campaign.goalAmount || 0) / 100),
        expires: campaign.expires
          ? toLocalDateTimeString(new Date(campaign.expires))
          : "",
        tagline: campaign.tagline || "",
        colorTheme: campaign.colorTheme || "#F4B942",
        logo: campaign.logo || "",
        alertTitle: campaign.alertTitle || "",
        alertDescription: campaign.alertDescription || "",
        alertImage: campaign.alertImage || "",
        alertButtonLabel: campaign.alertButtonLabel || "",
        enableAlertBanner: !!(campaign.alertTitle || campaign.alertDescription),
        enableMatchingDonors: !!(
          campaign.sponsorBonusMultiple && campaign.maxBonus
        ),
        matchPoolAmount: campaign.maxBonus
          ? String(campaign.maxBonus / 100)
          : "0",
        hideDonationAmounts: campaign.hideDonationAmounts || false,
      };

      setName(values.name);
      setDescription(values.description);
      setGoalAmount(values.goalAmount);
      setExpires(values.expires);
      setTagline(values.tagline);
      setColorTheme(values.colorTheme);
      setLogo(values.logo);
      setAlertTitle(values.alertTitle);
      setAlertDescription(values.alertDescription);
      setAlertImage(values.alertImage);
      setAlertButtonLabel(values.alertButtonLabel);
      setEnableAlertBanner(values.enableAlertBanner);
      setEnableMatchingDonors(values.enableMatchingDonors);
      setMatchPoolAmount(values.matchPoolAmount);
      setHideDonationAmounts(values.hideDonationAmounts);
      setOriginalValues(values);

      if (adminToken) {
        loadRecipientCount(campaign.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign, adminToken]);

  const loadRecipientCount = async (campaignId: string) => {
    if (!adminToken) return;
    try {
      const count = await getRecipientCount(adminToken, campaignId);
      setRecipientCount(count.reachableCount);
    } catch (error) {
      console.error("Failed to load recipient count:", error);
    }
  };

  const handleImageUpload = async (
    file: LogoFile,
    type: "logo" | "alertImage"
  ) => {
    if (!adminToken || !selectedClientId) return;
    try {
      const fileType = type === "logo" ? "CAMPAIGN_LOGO" : "ALERT_IMAGE";

      const result = await axios.post(
        `${rootUrl}/admin/upload`,
        {
          file: file.base64,
          type: fileType,
          mimeType: file.type,
          name: file.name,
          clientId: selectedClientId,
        },
        {
          headers: {
            Authorization: `Bearer ${adminToken}`,
          },
        }
      );

      const imageUrl = result.data.url;

      if (type === "logo") setLogo(imageUrl);
      else setAlertImage(imageUrl);

      setHasUnsavedChanges(true);

      toast({
        title: "Image uploaded",
        description: "Don't forget to save your changes",
        status: "info",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Failed to upload image",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleOpenImagePicker = (type: "logo" | "alertImage") => {
    setCurrentImageType(type);
    onPickerOpen();
  };

  const handleSelectFromLibrary = (url: string) => {
    if (currentImageType === "logo") setLogo(url);
    else setAlertImage(url);
    setHasUnsavedChanges(true);
  };

  const handleStartEdit = (section: EditableSection) => {
    if (hasUnsavedChanges) {
      const confirm = window.confirm(
        "You have unsaved changes. Starting a new edit will discard them. Continue?"
      );
      if (!confirm) return;
      handleCancelEdit();
    }
    setEditingSection(section);
  };

  const handleCancelEdit = () => {
    // Revert to original values
    setName(originalValues.name);
    setDescription(originalValues.description);
    setGoalAmount(originalValues.goalAmount);
    setExpires(originalValues.expires);
    setTagline(originalValues.tagline);
    setColorTheme(originalValues.colorTheme);
    setLogo(originalValues.logo);
    setAlertTitle(originalValues.alertTitle);
    setAlertDescription(originalValues.alertDescription);
    setAlertImage(originalValues.alertImage);
    setAlertButtonLabel(originalValues.alertButtonLabel);
    setEnableAlertBanner(originalValues.enableAlertBanner);
    setEnableMatchingDonors(originalValues.enableMatchingDonors);
    setMatchPoolAmount(originalValues.matchPoolAmount);
    setHideDonationAmounts(originalValues.hideDonationAmounts);
    setEditingSection(null);
    setHasUnsavedChanges(false);
  };

  const handleSaveSection = async () => {
    if (!adminToken) return;

    // Validate required fields if editing basic section
    if (editingSection === "basic") {
      if (!name || !description || !logo) {
        toast({
          title: "Please fill in required fields (name, description, logo)",
          status: "warning",
          duration: 3000,
        });
        return;
      }
    }

    try {
      let data: any = {};

      if (editingSection === "basic") {
        data = {
          name,
          description,
          logo,
          goalAmount: Math.round(parseFloat(goalAmount) * 100),
        };
      } else if (editingSection === "timing") {
        // Convert local datetime to UTC ISO string
        let expiresUTC = null;
        if (showCountdown && expires) {
          expiresUTC = new Date(expires).toISOString();
        }

        data = {
          expires: expiresUTC,
          tagline: tagline || undefined,
        };
      } else if (editingSection === "branding") {
        data = {
          colorTheme: colorTheme || undefined,
        };
      } else if (editingSection === "features") {
        data = {
          alertTitle: enableAlertBanner ? alertTitle || undefined : null,
          alertDescription: enableAlertBanner
            ? alertDescription || undefined
            : null,
          alertImage: enableAlertBanner ? alertImage || undefined : null,
          alertButtonLabel: enableAlertBanner
            ? alertButtonLabel || undefined
            : null,
          sponsorBonusMultiple: enableMatchingDonors ? 1 : null,
          maxBonus: enableMatchingDonors
            ? Math.round(parseFloat(matchPoolAmount) * 100)
            : null,
          hideDonationAmounts,
        };
      }

      const result = await updateCampaign(adminToken, campaign.id, data);

      // Update original values with new saved values
      const newOriginalValues = { ...originalValues };
      Object.keys(data).forEach((key) => {
        if (key === "goalAmount") {
          newOriginalValues.goalAmount = String(data.goalAmount / 100);
        } else {
          newOriginalValues[key] = data[key];
        }
      });
      setOriginalValues(newOriginalValues);

      toast({
        title: "Section updated successfully",
        status: "success",
        duration: 2000,
      });

      setEditingSection(null);
      setHasUnsavedChanges(false);

      // Update cache buster if logo or alert image was changed
      if (editingSection === "basic" || editingSection === "features") {
        setImageCacheBuster(Date.now());
      }

      onCampaignUpdate(result);
    } catch (error) {
      toast({
        title: "Failed to update campaign",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDelete = async () => {
    if (!adminToken) return;
    setIsDeleting(true);
    try {
      await deleteCampaign(adminToken, campaign.id);
      toast({
        title: "Campaign deleted successfully",
        status: "success",
        duration: 3000,
      });
      onDeleteClose();
      onCampaignUpdate(null);
      navigate("/admin/builder");
    } catch (error) {
      toast({
        title: "Failed to delete campaign",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Format currency
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(cents / 100);
  };

  // Format date
  const formatDate = (dateStr: string | Date | undefined) => {
    if (!dateStr) return "No expiration";
    const date = typeof dateStr === "string" ? new Date(dateStr) : dateStr;
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <Container>
      <VStack spacing={4} align="stretch" width="100%" maxW="100%">
        {/* Unsaved Changes Warning */}
        {hasUnsavedChanges && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Unsaved Changes</AlertTitle>
              <AlertDescription>
                You have unsaved changes in the {editingSection} section. Save
                or cancel to continue.
              </AlertDescription>
            </Box>
            <CloseButton
              position="absolute"
              right="8px"
              top="8px"
              onClick={handleCancelEdit}
            />
          </Alert>
        )}

        {/* Campaign Summary */}
        <SummaryBox>
          <VStack align="stretch" spacing={{ base: 2, md: 3 }}>
            <Box>
              <HStack mb={2} flexWrap="wrap" gap={2}>
                <Heading size={{ base: "xs", md: "md" }}>
                  {campaign.name}
                </Heading>
                <Badge colorScheme="green" fontSize={{ base: "xs", md: "sm" }}>
                  Active
                </Badge>
              </HStack>
              <Text fontSize={{ base: "xs", md: "sm" }} opacity={0.9}>
                {campaign.description}
              </Text>
            </Box>
            <HStack spacing={2} flexWrap="wrap" gap={2}>
              <Button
                size="sm"
                variant="solid"
                bg="white"
                color="purple.600"
                _hover={{ bg: "whiteAlpha.900", transform: "scale(1.05)" }}
                boxShadow="0 2px 8px rgba(0,0,0,0.15)"
                fontWeight="600"
                leftIcon={<ExternalLinkIcon />}
                transition="all 0.2s"
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_CLIENT_URL || ""}/campaign/${
                      campaign.id
                    }`,
                    "_blank"
                  )
                }
                width={{ base: "100%", md: "auto" }}
              >
                Preview
              </Button>
              <Button
                size="sm"
                variant="solid"
                bg="red.500"
                color="white"
                _hover={{ bg: "red.600" }}
                leftIcon={<DeleteIcon />}
                onClick={onDeleteOpen}
                width={{ base: "100%", md: "auto" }}
              >
                Delete
              </Button>
            </HStack>
          </VStack>

          <HStack
            spacing={{ base: 3, md: 6 }}
            flexWrap="wrap"
            mt={{ base: 2, md: 3 }}
            gap={2}
          >
            <Box minW="fit-content">
              <Text fontSize="xs" opacity={0.8}>
                Goal
              </Text>
              <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold">
                {formatCurrency(campaign.goalAmount || 0)}
              </Text>
            </Box>
            <Box minW="fit-content">
              <Text fontSize="xs" opacity={0.8}>
                Expires
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="600">
                {formatDate(campaign.expires || "")}
              </Text>
            </Box>
            {recipientCount !== null && (
              <Box minW="fit-content">
                <Text fontSize="xs" opacity={0.8}>
                  Recipients
                </Text>
                <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold">
                  {recipientCount}
                </Text>
              </Box>
            )}
          </HStack>
        </SummaryBox>

        {/* Message Scheduling */}
        <Box
          p={{ base: 3, md: 4 }}
          bg="blue.50"
          borderRadius="md"
          border="1px solid"
          borderColor="blue.200"
          mb={{ base: 3, md: 4 }}
        >
          <HStack
            mb={{ base: 2, md: 3 }}
            justify="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <HStack>
              <ChatIcon color="blue.700" boxSize={4} />
              <Heading size={{ base: "xs", md: "sm" }} color="blue.700">
                Messages
              </Heading>
            </HStack>
            <Badge colorScheme="blue" fontSize={{ base: "xs", md: "sm" }}>
              {recipientCount || 0} recipients
            </Badge>
          </HStack>
          <MessageBuilder campaign={campaign} recipientCount={recipientCount} />
        </Box>

        <Heading
          size={{ base: "xs", md: "sm" }}
          color="gray.700"
          mb={{ base: 2, md: 3 }}
        >
          Campaign Details
        </Heading>

        {/* Basic Information */}
        <EditableSectionBox
          $isEditing={editingSection === "basic"}
          $color="#3182ce"
        >
          <HStack
            justify="space-between"
            mb={editingSection === "basic" ? 4 : 0}
            flexWrap="wrap"
            gap={2}
          >
            <HStack spacing={2}>
              <InfoIcon color="#3182ce" boxSize={4} />
              <Heading size={{ base: "xs", md: "sm" }}>
                Basic Information
              </Heading>
            </HStack>
            {editingSection !== "basic" ? (
              <IconButton
                aria-label="Edit basic info"
                icon={<EditIcon />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={() => handleStartEdit("basic")}
                isDisabled={editingSection !== null}
                flexShrink={0}
              />
            ) : (
              <HStack flexShrink={0}>
                <IconButton
                  aria-label="Save"
                  icon={<CheckIcon />}
                  size="sm"
                  colorScheme="green"
                  onClick={handleSaveSection}
                />
                <IconButton
                  aria-label="Cancel"
                  icon={<CloseIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelEdit}
                />
              </HStack>
            )}
          </HStack>

          {editingSection === "basic" ? (
            <VStack spacing={4} align="stretch">
              <Section>
                <FormControl isRequired>
                  <FormLabel>Campaign Name</FormLabel>
                  <FormHelperText mb={2}>
                    Appears in SMS messages and on the campaign donation page
                  </FormHelperText>
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="Annual Gala 2025"
                  />
                </FormControl>
              </Section>

              <Section>
                <FormControl isRequired>
                  <FormLabel>Description</FormLabel>
                  <FormHelperText mb={2}>
                    Tell donors about your cause and its impact (2-3 sentences)
                  </FormHelperText>
                  <Textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    placeholder="Help us raise funds for..."
                    rows={3}
                  />
                </FormControl>
              </Section>

              <Section>
                <FormControl isRequired>
                  <FormLabel>Campaign Icon (Square 1:1)</FormLabel>
                  <FormHelperText mb={2}>
                    Square image displayed in messages and page header
                  </FormHelperText>
                  <Button
                    onClick={() => handleOpenImagePicker("logo")}
                    size="sm"
                    variant="outline"
                    leftIcon={<AttachmentIcon />}
                    mb={2}
                  >
                    Select from Library
                  </Button>
                  <DragDrop
                    onLogoChange={(file) => handleImageUpload(file, "logo")}
                    enforceSquare={true}
                    label="Upload or drop square campaign icon"
                  />
                  {logo && (
                    <ImagePreview src={`${logo}?t=${imageCacheBuster}`} alt="Campaign icon" $square />
                  )}
                </FormControl>
              </Section>

              <Section>
                <FormControl isRequired>
                  <FormLabel>Fundraising Goal ($)</FormLabel>
                  <FormHelperText mb={2}>
                    Your fundraising target in USD
                  </FormHelperText>
                  <NumberInput
                    value={goalAmount}
                    onChange={(val) => {
                      setGoalAmount(val);
                      setHasUnsavedChanges(true);
                    }}
                    min={0}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
              </Section>
            </VStack>
          ) : (
            <Box mt={4}>
              <DetailItem>
                <Text fontWeight="600" color="gray.600" fontSize="sm" mb={1}>
                  Name
                </Text>
                <Text fontSize="md">{campaign.name}</Text>
              </DetailItem>
              <DetailItem>
                <Text fontWeight="600" color="gray.600" fontSize="sm" mb={1}>
                  Description
                </Text>
                <Text fontSize="md">{campaign.description}</Text>
              </DetailItem>
              <DetailGrid>
                <DetailItem>
                  <Text fontWeight="600" color="gray.600" fontSize="sm" mb={1}>
                    Goal
                  </Text>
                  <Text fontSize="md">
                    {formatCurrency(campaign.goalAmount || 0)}
                  </Text>
                </DetailItem>
                {campaign.logo && (
                  <DetailItem>
                    <Text
                      fontWeight="600"
                      color="gray.600"
                      fontSize="sm"
                      mb={2}
                    >
                      Icon
                    </Text>
                    <ImagePreview
                      src={campaign.logo}
                      alt="Campaign icon"
                      $square
                    />
                  </DetailItem>
                )}
              </DetailGrid>
            </Box>
          )}
        </EditableSectionBox>

        {/* Grid for smaller sections */}
        <SectionsGrid>
          {/* Timing & Urgency */}
          <EditableSectionBox
            $isEditing={editingSection === "timing"}
            $color="#805ad5"
          >
            <HStack
              justify="space-between"
              mb={editingSection === "timing" ? 4 : 0}
              flexWrap="wrap"
              gap={2}
            >
              <HStack spacing={2}>
                <CalendarIcon color="#805ad5" boxSize={4} />
                <Heading size={{ base: "xs", md: "sm" }}>
                  Timing & Urgency
                </Heading>
              </HStack>
              {editingSection !== "timing" ? (
                <IconButton
                  aria-label="Edit timing"
                  icon={<EditIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  onClick={() => handleStartEdit("timing")}
                  isDisabled={editingSection !== null}
                />
              ) : (
                <HStack>
                  <IconButton
                    aria-label="Save"
                    icon={<CheckIcon />}
                    size="sm"
                    colorScheme="green"
                    onClick={handleSaveSection}
                  />
                  <IconButton
                    aria-label="Cancel"
                    icon={<CloseIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={handleCancelEdit}
                  />
                </HStack>
              )}
            </HStack>

            {editingSection === "timing" ? (
              <VStack spacing={4} align="stretch">
                <Section>
                  <FormControl>
                    <FormLabel>Expiration Date & Time (Local)</FormLabel>
                    <FormHelperText mb={2}>
                      When campaign ends - enables countdown timer on donation
                      page
                    </FormHelperText>
                    <Input
                      type="datetime-local"
                      value={expires}
                      onChange={(e) => {
                        setExpires(e.target.value);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </FormControl>
                </Section>

                {expires && (
                  <Section>
                    <FormControl>
                      <Checkbox
                        isChecked={showCountdown}
                        onChange={(e) => {
                          setShowCountdown(e.target.checked);
                          setHasUnsavedChanges(true);
                        }}
                      >
                        Show countdown timer on campaign page
                      </Checkbox>
                      <Text fontSize="sm" color="gray.600" ml={6} mt={1}>
                        Displays time remaining to create urgency
                      </Text>
                    </FormControl>
                  </Section>
                )}

                <Section>
                  <FormControl>
                    <FormLabel>Tagline (Optional)</FormLabel>
                    <FormHelperText mb={2}>
                      Short, emotional phrase (5-7 words) displayed as a banner
                    </FormHelperText>
                    <Input
                      value={tagline}
                      onChange={(e) => {
                        setTagline(e.target.value);
                        setHasUnsavedChanges(true);
                      }}
                      placeholder="Every gift makes a difference"
                    />
                  </FormControl>
                </Section>
              </VStack>
            ) : (
              <Box mt={4}>
                <DetailItem>
                  <Text fontWeight="600" color="gray.600" fontSize="sm" mb={1}>
                    Expires
                  </Text>
                  <Text fontSize="md">
                    {formatDate(campaign.expires || "")}
                  </Text>
                </DetailItem>
                {campaign.tagline && (
                  <DetailItem>
                    <Text
                      fontWeight="600"
                      color="gray.600"
                      fontSize="sm"
                      mb={1}
                    >
                      Tagline
                    </Text>
                    <Text fontSize="md">{campaign.tagline}</Text>
                  </DetailItem>
                )}
              </Box>
            )}
          </EditableSectionBox>

          {/* Branding */}
          <EditableSectionBox
            $isEditing={editingSection === "branding"}
            $color="#dd6b20"
          >
            <HStack
              justify="space-between"
              mb={editingSection === "branding" ? 4 : 0}
            >
              <HStack spacing={2}>
                <SettingsIcon color="#dd6b20" boxSize={4} />
                <Heading size="sm">Branding</Heading>
              </HStack>
              {editingSection !== "branding" ? (
                <IconButton
                  aria-label="Edit branding"
                  icon={<EditIcon />}
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                  onClick={() => handleStartEdit("branding")}
                  isDisabled={editingSection !== null}
                />
              ) : (
                <HStack>
                  <IconButton
                    aria-label="Save"
                    icon={<CheckIcon />}
                    size="sm"
                    colorScheme="green"
                    onClick={handleSaveSection}
                  />
                  <IconButton
                    aria-label="Cancel"
                    icon={<CloseIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={handleCancelEdit}
                  />
                </HStack>
              )}
            </HStack>

            {editingSection === "branding" ? (
              <VStack spacing={4} align="stretch">
                <Section>
                  <FormControl>
                    <FormLabel>Color Theme</FormLabel>
                    <FormHelperText mb={2}>
                      Primary color for campaign page and donation buttons
                    </FormHelperText>
                    <Input
                      type="color"
                      value={colorTheme}
                      onChange={(e) => {
                        setColorTheme(e.target.value);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </FormControl>
                </Section>

                <Box p={4} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="600" mb={2}>
                    Preview:
                  </Text>
                  <Box
                    p={3}
                    bg={colorTheme}
                    color="white"
                    borderRadius="md"
                    textAlign="center"
                  >
                    Sample Donate Button
                  </Box>
                </Box>
              </VStack>
            ) : (
              <Box mt={4}>
                <DetailItem>
                  <Text fontWeight="600" color="gray.600" fontSize="sm" mb={2}>
                    Color Theme
                  </Text>
                  <HStack>
                    <Box
                      w="28px"
                      h="28px"
                      bg={campaign.colorTheme || "#F4B942"}
                      borderRadius="6px"
                      border="2px solid"
                      borderColor="gray.300"
                    />
                    <Text fontSize="md">
                      {campaign.colorTheme || "#F4B942"}
                    </Text>
                  </HStack>
                </DetailItem>
              </Box>
            )}
          </EditableSectionBox>
        </SectionsGrid>

        {/* Optional Features - Full Width */}
        <EditableSectionBox
          $isEditing={editingSection === "features"}
          $color="#d69e2e"
        >
          <HStack
            justify="space-between"
            mb={editingSection === "features" ? 4 : 0}
          >
            <HStack spacing={2}>
              <StarIcon color="#d69e2e" boxSize={4} />
              <Heading size="sm">Optional Features</Heading>
            </HStack>
            {editingSection !== "features" ? (
              <IconButton
                aria-label="Edit features"
                icon={<EditIcon />}
                size="sm"
                variant="ghost"
                colorScheme="blue"
                onClick={() => handleStartEdit("features")}
                isDisabled={editingSection !== null}
              />
            ) : (
              <HStack>
                <IconButton
                  aria-label="Save"
                  icon={<CheckIcon />}
                  size="sm"
                  colorScheme="green"
                  onClick={handleSaveSection}
                />
                <IconButton
                  aria-label="Cancel"
                  icon={<CloseIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelEdit}
                />
              </HStack>
            )}
          </HStack>

          {editingSection === "features" ? (
            <VStack spacing={6} align="stretch">
              <Section>
                <Checkbox
                  isChecked={enableAlertBanner}
                  onChange={(e) => {
                    setEnableAlertBanner(e.target.checked);
                    setHasUnsavedChanges(true);
                  }}
                  size="lg"
                  mb={3}
                >
                  <Text fontWeight="600">Enable Alert Banner</Text>
                </Checkbox>
                <Text fontSize="sm" color="gray.600" ml={6} mb={4}>
                  Promotional banner at top of page (e.g., "Matching gifts
                  available!")
                </Text>

                {enableAlertBanner && (
                  <Box
                    pl={6}
                    borderLeft="3px solid"
                    borderColor="blue.200"
                    ml={3}
                  >
                    <Section>
                      <FormControl>
                        <FormLabel>Alert Title</FormLabel>
                        <Input
                          value={alertTitle}
                          onChange={(e) => {
                            setAlertTitle(e.target.value);
                            setHasUnsavedChanges(true);
                          }}
                          placeholder="🔥 Matching gifts available!"
                        />
                      </FormControl>
                    </Section>

                    <Section>
                      <FormControl>
                        <FormLabel>Alert Description</FormLabel>
                        <Textarea
                          value={alertDescription}
                          onChange={(e) => {
                            setAlertDescription(e.target.value);
                            setHasUnsavedChanges(true);
                          }}
                          placeholder="All donations matched 2x until midnight"
                          rows={2}
                        />
                      </FormControl>
                    </Section>

                    <Section>
                      <FormControl>
                        <FormLabel>Alert Image</FormLabel>
                        <Button
                          onClick={() => handleOpenImagePicker("alertImage")}
                          size="sm"
                          variant="outline"
                          leftIcon={<AttachmentIcon />}
                          mb={2}
                        >
                          Select from Library
                        </Button>
                        <DragDrop
                          onLogoChange={(file) =>
                            handleImageUpload(file, "alertImage")
                          }
                          label="Upload or drop alert banner image"
                        />
                        {alertImage && (
                          <ImagePreview src={`${alertImage}?t=${imageCacheBuster}`} alt="Alert image" />
                        )}
                      </FormControl>
                    </Section>

                    <Section>
                      <FormControl>
                        <FormLabel>Alert Button Label</FormLabel>
                        <Input
                          value={alertButtonLabel}
                          onChange={(e) => {
                            setAlertButtonLabel(e.target.value);
                            setHasUnsavedChanges(true);
                          }}
                          placeholder="Donate Now"
                        />
                      </FormControl>
                    </Section>
                  </Box>
                )}
              </Section>

              <Box>
                <Checkbox
                  isChecked={enableMatchingDonors}
                  onChange={(e) => {
                    setEnableMatchingDonors(e.target.checked);
                    setHasUnsavedChanges(true);
                  }}
                  size="lg"
                  mb={3}
                >
                  <Text fontWeight="600">
                    This campaign has matching donors
                  </Text>
                </Checkbox>
                <Text fontSize="sm" color="gray.600" ml={6} mb={4}>
                  Enable if sponsors will match donations 1:1 until pool is
                  exhausted
                </Text>

                {enableMatchingDonors && (
                  <Box
                    pl={6}
                    borderLeft="3px solid"
                    borderColor="green.200"
                    ml={3}
                  >
                    <Section>
                      <FormControl>
                        <FormLabel>Total Match Pool (USD)</FormLabel>
                        <FormHelperText mb={2}>
                          All donations matched 1:1 until this amount is reached
                        </FormHelperText>
                        <NumberInput
                          value={matchPoolAmount}
                          onChange={(val) => {
                            setMatchPoolAmount(val);
                            setHasUnsavedChanges(true);
                          }}
                          min={0}
                        >
                          <NumberInputField placeholder="5000" />
                        </NumberInput>
                      </FormControl>
                    </Section>

                    {parseFloat(matchPoolAmount) > 0 && (
                      <Box p={3} bg="green.50" borderRadius="md" mt={3}>
                        <Text fontSize="sm" fontWeight="600" color="green.700">
                          <strong>Matching Preview:</strong> Sponsors will match
                          donations 1:1 up to $
                          {parseFloat(matchPoolAmount).toLocaleString()} total
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Box>
                <Checkbox
                  isChecked={hideDonationAmounts}
                  onChange={(e) => {
                    setHideDonationAmounts(e.target.checked);
                    setHasUnsavedChanges(true);
                  }}
                  size="lg"
                  mb={3}
                >
                  <Text fontWeight="600">
                    Hide donation amounts in recent donations
                  </Text>
                </Checkbox>
                <Text fontSize="sm" color="gray.600" ml={6} mb={4}>
                  When enabled, recent donations will show messages like "John
                  Doe just gave" instead of showing the dollar amount
                </Text>
              </Box>
            </VStack>
          ) : (
            <DetailGrid>
              <DetailItem>
                <Text fontWeight="600" color="gray.600" fontSize="sm" mb={1}>
                  Alert Banner
                </Text>
                <Text fontSize="md" mb={2}>
                  {campaign.alertTitle ? "Enabled" : "Disabled"}
                </Text>
                {campaign.alertTitle && (
                  <Box
                    p={3}
                    bg="blue.50"
                    borderRadius="md"
                    borderLeft="3px solid"
                    borderLeftColor="blue.400"
                  >
                    <Text fontSize="sm" color="gray.700" mb={1}>
                      <strong>Title:</strong> {campaign.alertTitle}
                    </Text>
                    {campaign.alertDescription && (
                      <Text fontSize="sm" color="gray.700">
                        <strong>Description:</strong>{" "}
                        {campaign.alertDescription}
                      </Text>
                    )}
                  </Box>
                )}
              </DetailItem>
              <DetailItem>
                <Text fontWeight="600" color="gray.600" fontSize="sm" mb={1}>
                  Matching Donors
                </Text>
                <Text fontSize="md">
                  {campaign.sponsorBonusMultiple && campaign.maxBonus
                    ? `Enabled (${formatCurrency(campaign.maxBonus)} pool)`
                    : "Disabled"}
                </Text>
              </DetailItem>
              <DetailItem>
                <Text fontWeight="600" color="gray.600" fontSize="sm" mb={1}>
                  Hide Donation Amounts
                </Text>
                <Text fontSize="md">
                  {campaign.hideDonationAmounts ? "Enabled" : "Disabled"}
                </Text>
              </DetailItem>
            </DetailGrid>
          )}
        </EditableSectionBox>
      </VStack>

      {/* Delete Confirmation Modal */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Campaign
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={4}>
                Are you sure you want to delete <strong>{campaign.name}</strong>
                ?
              </Text>
              <HStack color="red.600" fontWeight="600" mb={2}>
                <WarningIcon />
                <Text>This is a destructive action!</Text>
              </HStack>
              <Text mt={2}>
                All associated data will be permanently deleted:
              </Text>
              <Box as="ul" pl={6} mt={2}>
                <li>Campaign details and settings</li>
                <li>All scheduled messages</li>
                <li>Message history and analytics</li>
              </Box>
              <Text mt={4} fontStyle="italic" color="gray.600">
                This action cannot be undone.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
              >
                Delete Campaign
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Image Asset Picker */}
      <ImageAssetPicker
        isOpen={isPickerOpen}
        onClose={onPickerClose}
        clientId={selectedClientId}
        onSelectImage={handleSelectFromLibrary}
      />
    </Container>
  );
};
